import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { AnimatedPageLoader } from 'smartsuite-ui';
import { getDataPromise } from 'brock-fetch-utils';
import { useLabelContext } from '../shared/hooks/labelContext';
import Editor from './Editor';
import { handleErrorResult } from '../viewDashboards/utils/fetchUtils';
import mapConfigurationVersion from '../viewDashboards/utils/configVersionMappers/mapConfigurationVersion';
import widgetDrawConfigurations from '../viewDashboards/widgets/widgetDrawConfigurations';
import {
    defaultUserDashboardConfig,
    setupViewAndWidgetConfigurations,
} from '../viewDashboards/utils/configurationAndValidationUtils';
import { getFormattedWidget } from './customDashboardUtils';
import DashboardContext from '../shared/hooks/dashboardContext';

function getAvailableWidgets(viewConfigurations, dashboardLabels) {
    return viewConfigurations.reduce(
        (result, view) => [
            ...result,
            ...view.widgets.map((widget) =>
                getFormattedWidget(view, widget, dashboardLabels)
            ),
        ],
        []
    );
}

// eslint-disable-next-line complexity
export default function EditorLayout(props) {
    const { dashboardLabels } = useLabelContext();
    const { customizations, uriConstants } = useContext(DashboardContext);
    const [state, setState] = useState({ error: props.errorMessage });

    const setError = useCallback((error) => {
        setState((prevState) => ({ ...prevState, error }));
    }, []);

    function getVersionMappedDashboardConfigs(dashboardConfigs) {
        return dashboardConfigs.map((dashboard) => ({
            ...dashboard,
            configuration: mapConfigurationVersion(dashboard.configuration),
        }));
    }

    function getViewConfigurations(
        versionMappedDashboardConfigs,
        widgetRenderers
    ) {
        return versionMappedDashboardConfigs.reduce(
            (result, dashboardConfig) => [
                ...result,
                ...dashboardConfig.configuration.views.map((view) => ({
                    ...setupViewAndWidgetConfigurations(widgetRenderers, view),
                    dashboardKey: dashboardConfig.id,
                })),
            ],
            []
        );
    }

    useEffect(() => {
        function getCurrentDashboardConfiguration(userDashboardConfig) {
            if (props.isCreateMode && !props.dashboardConfiguration) {
                return defaultUserDashboardConfig();
            }

            return userDashboardConfig ?? props.dashboardConfiguration;
        }

        function initialize() {
            const userDashboardConfigPromise =
                props.isCreateMode || props.dashboardConfiguration
                    ? undefined
                    : getDataPromise(props.customDashboardConfigurationUri);

            const availableWidgetsPromise = getDataPromise(
                uriConstants.getAllDashboardConfigurations
            ).then((allDashboardConfigs) => {
                const versionMappedDashboardConfigs =
                    getVersionMappedDashboardConfigs(allDashboardConfigs);

                const widgetRenderers = {
                    ...widgetDrawConfigurations,
                    ...customizations.widgetRenderers,
                };

                const viewConfigurations = getViewConfigurations(
                    versionMappedDashboardConfigs,
                    widgetRenderers
                );

                const availableWidgets = getAvailableWidgets(
                    viewConfigurations,
                    dashboardLabels
                );

                return availableWidgets;
            });

            Promise.all([
                availableWidgetsPromise,
                userDashboardConfigPromise,
            ]).then(
                async ([availableWidgets, userDashboardConfig]) => {
                    setState({
                        isLoaded: true,
                        availableWidgets,
                        dashboardConfiguration:
                            getCurrentDashboardConfiguration(
                                userDashboardConfig
                            ),
                    });
                },
                (error) => handleErrorResult(error.httpResult, setError)
            );
        }

        initialize();
    }, [
        dashboardLabels,
        customizations.widgetRenderers,
        uriConstants,
        setError,
        props.customDashboardConfigurationUri,
        props.dashboardConfiguration,
        props.isCreateMode,
    ]);

    if (state.error) {
        throw Error(state.error);
    }

    if (!state.isLoaded) {
        return <AnimatedPageLoader />;
    }

    if (state.dashboardConfiguration) {
        return (
            <Editor
                availableWidgets={state.availableWidgets}
                dashboardConfiguration={state.dashboardConfiguration}
                dashboardKey={props.dashboardKey}
                deleteDashboard={props.deleteDashboard}
                hasPendingChanges={props.hasPendingChanges}
                isCreateMode={props.isCreateMode}
                navigateToCustomDashboard={props.navigateToCustomDashboard}
                saveDashboard={props.saveDashboard}
                selectedWidgetId={props.selectedWidgetId}
            />
        );
    }

    return null;
}

EditorLayout.propTypes = {
    customDashboardConfigurationUri: PropTypes.string,
    dashboardConfiguration: PropTypes.instanceOf(Object),
    dashboardKey: PropTypes.string,
    deleteDashboard: PropTypes.func,
    errorMessage: PropTypes.string,
    hasPendingChanges: PropTypes.bool,
    isCreateMode: PropTypes.bool,
    navigateToCustomDashboard: PropTypes.func.isRequired,
    saveDashboard: PropTypes.func.isRequired,
    selectedWidgetId: PropTypes.string,
};
